import { useEffect, useState, Suspense, lazy } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { auth } from "../../../Config/firebase";
import {
  checkActionCode,
  applyActionCode,
  verifyPasswordResetCode,
} from "firebase/auth";
import { toast } from "react-fox-toast";

const ResetPasswordConfirm = lazy(() => import('../pages/ResetPasswordConfirm'));

export const AuthActionHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actionResult, setActionResult] = useState(null);

  const mode = searchParams.get('mode');
  const actionCode = searchParams.get('oobCode');
  const continueUrl = searchParams.get('continueUrl');

  useEffect(() => {
    setActionResult(null);
    setLoading(true);

    if (!mode || !actionCode) {
      toast.error("Invalid action link. Missing required parameters.");
      setLoading(false);
      navigate('/');
      return;
    }

    const handleAction = async () => {
      try {
        switch (mode) {
          case 'resetPassword':
            await verifyPasswordResetCode(auth, actionCode);
            toast.info("Please enter your new password");
            setLoading(false);
            break;

          case 'verifyEmail':
            await applyActionCode(auth, actionCode);
            toast.success("Your email address has been successfully verified!");
            setActionResult({ type: 'verifyEmail', success: true });
            break;

          case 'recoverEmail': {
            const info = await checkActionCode(auth, actionCode);
            const restoredEmail = info.data.email;
            await applyActionCode(auth, actionCode);
            toast.success(`Your email address has been reverted to ${restoredEmail}`);
            setActionResult({ 
              type: 'recoverEmail', 
              success: true, 
              email: restoredEmail 
            });
            break;
          }

          default:
            toast.error(`Unknown action mode: ${mode}`);
            navigate('/');
        }
      } catch (err) {
        console.error(`Error handling action '${mode}':`, err);
        let errorMessage = `Failed to ${
          mode === 'resetPassword' ? 'initialize password reset' : 
          mode === 'verifyEmail' ? 'verify email' : 'recover email'
        }. The link may be invalid or expired.`;
        
        toast.error(errorMessage);
        
        if (mode === 'verifyEmail') {
          setActionResult({ 
            type: 'verifyEmail', 
            success: false, 
            error: errorMessage 
          });
        }
        if (mode === 'recoverEmail') {
          setActionResult({ 
            type: 'recoverEmail', 
            success: false, 
            error: errorMessage 
          });
        }
      } finally {
        if (mode !== 'resetPassword') {
          setLoading(false);
        }
      }
    };

    handleAction();
  }, [mode, actionCode, navigate, continueUrl]);

  if (loading && mode !== 'resetPassword') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p>Processing your request...</p>
        </div>
      </div>
    );
  }

  if (actionResult?.type === 'verifyEmail') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white p-4">
        <div className="max-w-md w-full space-y-4 bg-white p-8 rounded-2xl shadow-lg text-center">
          <h2 className="text-2xl font-bold text-green-600">Email Verified</h2>
          <p className="text-gray-700">You can now enjoy all features of our service.</p>
          <Link 
            to={continueUrl || '/login'} 
            className="w-full inline-flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
          >
            {continueUrl ? "Continue" : "Go to Login"}
          </Link>
        </div>
      </div>
    );
  }

  if (actionResult?.type === 'recoverEmail') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white p-4">
        <div className="max-w-md w-full space-y-4 bg-white p-8 rounded-2xl shadow-lg text-center">
          <h2 className="text-2xl font-bold text-blue-600">Email Recovered</h2>
          <p className="text-gray-700">Your account email has been updated to <span className="font-semibold">{actionResult.email}</span></p>
          <div className="space-y-2">
            <Link 
              to={continueUrl || '/login'} 
              className="w-full inline-flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
            >
              {continueUrl ? "Continue" : "Go to Login"}
            </Link>
            <Link 
              to="/forgot-password" 
              state={{ email: actionResult.email }}
              className="text-sm text-violet-600 hover:text-violet-500"
            >
              Reset Password
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'resetPassword') {
    return (
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-600 mx-auto mb-4"></div>
            <p>Loading Password Reset...</p>
          </div>
        </div>
      }>
        <ResetPasswordConfirm actionCode={actionCode} continueUrl={continueUrl} />
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="text-center">
        <h2 className="text-xl font-bold text-red-600 mb-2">Invalid Link</h2>
        <p className="mb-4">Please check the link or contact support.</p>
        <Link 
          to="/" 
          className="inline-flex items-center text-violet-600 hover:text-violet-500"
        >
          Return to Homepage
        </Link>
      </div>
    </div>
  );
};